import { theme, FONT_RUNE } from "./theme";
import { createInitialState, FILES, RANKS, fromKey } from "../engine";

/**
 * Mini-board SVG used in the Instructions panel to show the starting position visually.
 * Renders the full 8x16 board with shape glyphs for every piece (no numbers — too small at this size).
 */
export function StartingBoardSVG() {
  const state = createInitialState();
  const cell = 26;
  const boardW = FILES * cell;
  const boardH = RANKS * cell;
  // Asymmetric padding: more space on the top and left to fit file/rank labels outside the board.
  const padTop = 16;
  const padLeft = 16;
  const padRight = 6;
  const padBottom = 6;

  return (
    <svg
      viewBox={`0 0 ${boardW + padLeft + padRight} ${boardH + padTop + padBottom}`}
      width="100%"
      role="img"
      aria-label="Rithmomachia starting position"
      style={{
        maxWidth: 260,
        height: "auto",
        display: "block",
        margin: "12px auto",
        background: theme.boardEdge,
        borderRadius: 6,
        border: `1px solid ${theme.boardEdge}`,
      }}
    >
      <g transform={`translate(${padLeft} ${padTop})`}>
        {/* Checkered squares */}
        {Array.from({ length: RANKS }).map((_, r) =>
          Array.from({ length: FILES }).map((__, f) => (
            <rect
              key={`bg-${f}-${r}`}
              x={f * cell}
              y={r * cell}
              width={cell}
              height={cell}
              fill={(f + r) % 2 === 0 ? theme.boardLight : theme.boardDark}
            />
          )),
        )}

        {/* Pieces */}
        {Array.from(state.board.entries()).map(([key, piece]) => {
          const sq = fromKey(key);
          const cx = sq.file * cell + cell / 2;
          const cy = sq.rank * cell + cell / 2;
          const fill = piece.side === "white" ? theme.whiteFill : theme.blackFill;
          const stroke = piece.side === "white" ? theme.whiteEdge : theme.blackEdge;
          const inner = cell * 0.7;

          if (piece.shape === "round") {
            return (
              <circle
                key={key}
                cx={cx}
                cy={cy}
                r={inner / 2}
                fill={fill}
                stroke={stroke}
                strokeWidth={1}
              />
            );
          }
          if (piece.shape === "triangle") {
            const h = inner * 0.866;
            const pts =
              piece.side === "white"
                ? `${cx},${cy - h * 0.55} ${cx + inner / 2},${cy + h * 0.45} ${cx - inner / 2},${cy + h * 0.45}`
                : `${cx},${cy + h * 0.55} ${cx + inner / 2},${cy - h * 0.45} ${cx - inner / 2},${cy - h * 0.45}`;
            return (
              <polygon key={key} points={pts} fill={fill} stroke={stroke} strokeWidth={1} />
            );
          }
          if (piece.shape === "square") {
            return (
              <rect
                key={key}
                x={cx - inner / 2}
                y={cy - inner / 2}
                width={inner}
                height={inner}
                fill={fill}
                stroke={stroke}
                strokeWidth={1}
              />
            );
          }
          // pyramid: square base + triangle peak
          const half = inner / 2;
          return (
            <g key={key}>
              <rect
                x={cx - half * 0.85}
                y={cy - half * 0.15}
                width={half * 1.7}
                height={half * 0.85}
                fill={fill}
                stroke={stroke}
                strokeWidth={1}
              />
              <polygon
                points={`${cx},${cy - half} ${cx + half * 0.85},${cy - half * 0.15} ${cx - half * 0.85},${cy - half * 0.15}`}
                fill={fill}
                stroke={stroke}
                strokeWidth={1}
              />
            </g>
          );
        })}

        {/* File labels (A..H) above the top row */}
        {Array.from({ length: FILES }).map((_, f) => (
          <text
            key={`file-${f}`}
            x={f * cell + cell / 2}
            y={-4}
            fill={theme.boardLabel}
            fontFamily={FONT_RUNE}
            fontSize={9}
            textAnchor="middle"
          >
            {"ABCDEFGH"[f]}
          </text>
        ))}

        {/* Rank labels (1..16) along the left edge */}
        {Array.from({ length: RANKS }).map((_, r) => (
          <text
            key={`rank-${r}`}
            x={-4}
            y={r * cell + cell / 2}
            fill={theme.boardLabel}
            fontFamily={FONT_RUNE}
            fontSize={9}
            textAnchor="end"
            dominantBaseline="middle"
          >
            {r + 1}
          </text>
        ))}

        {/* Board border */}
        <rect
          x={0}
          y={0}
          width={boardW}
          height={boardH}
          fill="none"
          stroke={theme.boardEdge}
          strokeWidth={1.5}
        />
      </g>
    </svg>
  );
}
